<template>
    <div class="collection-detail-container">
      <el-header>
        <div class="header-content">
          <el-button 
            icon="el-icon-back" 
            @click="goBack" 
            class="back-button"
          >
            Back
          </el-button>
          <h1>Collection Details</h1>
        </div>
      </el-header>
      <el-main>
        <el-card class="collection-info-card">
          <div v-if="loading" class="loading-container">
            <el-skeleton animated>
              <template>
                <div class="collection-header">
                  <el-skeleton-item variant="image" style="width: 200px; height: 283px"/>
                  <div style="flex: 1">
                    <el-skeleton-item variant="h1" style="width: 50%"/>
                    <el-skeleton-item variant="text" style="margin-top: 16px; width: 30%"/>
                    <el-skeleton-item variant="text" style="margin-top: 16px"/>
                    <el-skeleton-item variant="text" style="margin-top: 16px"/>
                  </div>
                </div>
              </template>
            </el-skeleton>
          </div>
          
          <template v-else>
            <div class="collection-header">
              <div class="cover-image">
                <img v-if="collection.cover_url" :src="collection.cover_url" alt="Collection Cover">
                <div v-else class="placeholder-cover">
                  <i class="el-icon-picture-outline"></i>
                </div>
              </div>
              <div class="collection-info">
                <h2>{{ collection.title }}</h2>
                <p class="author">By {{ collection.user_name }} | <span class="update-time">Latest update on: {{ new Date((collection.update_time || collection.create_time) * 1000).toLocaleDateString() }}</span></p>
                <p class="description">{{ collection.description }}</p>
                <p class="token-price">
                  <i class="el-icon-money"></i>
                  <span v-if="collection.usd_price === 0">FREE</span>
                  <span v-else-if="collection.usd_price !== null && collection.usd_price !== undefined">${{ collection.usd_price }}</span>
                  <span v-else>Not Priced Yet</span>
                </p>
              </div>
              <div class="edit-button-container" v-if="collection.user_id === $store.state.user_id">
                <el-button 
                  type="primary" 
                  icon="el-icon-edit"
                  @click="handleEdit"
                >
                  Edit
                </el-button>
              </div>
            </div>
  
            <div class="collection-stats">
              <div class="stats-left">
                <div class="stat-item">
                  <i class="el-icon-folder"></i>
                  <span>{{ collection.group_count || 0 }} Groups</span>
                </div>
                <div class="stat-item">
                  <i class="el-icon-document"></i>
                  <span>{{ collection.knowit_count || 0 }} Knowits</span>
                </div>
                <div class="stat-item">
                  <i class="el-icon-document-copy"></i>
                  <span>{{ collection.content_count || 0 }} Contents</span>
                </div>
              </div>
              <div class="stats-right">
                <div class="stat-item">
                  <i class="el-icon-view"></i>
                  <span>{{ collection.views || 0 }} Views</span>
                </div>
                <div class="stat-item">
                  <i class="el-icon-star-off"></i>
                  <span>{{ collection.likes || 0 }} Likes</span>
                </div>
                <div class="stat-item">
                  <i class="el-icon-shopping-cart-2"></i>
                  <span>{{ collection.purchased || 0 }} Purchases</span>
                </div>
              </div>
            </div>
  
            <div class="groups-container">
              <div class="groups-header">
                <el-button
                  type="text"
                  :icon="areAllGroupsCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"
                  @click="toggleAllGroups"
                >
                  {{ areAllGroupsCollapsed ? 'Unfold All' : 'Fold All' }}
                </el-button>
              </div>
              <div v-for="(group, groupIndex) in collection.groups || []" :key="groupIndex" class="group-section">
                <div class="group-header">
                <h3>{{ groupIndex + 1 }}. {{ group.title }} 
                  <span class="knowit-count">
                    {{ (group.knowits || []).length }} 
                    {{ (group.knowits || []).length === 1 || (group.knowits || []).length === 0 ? 'Knowit' : 'Knowits' }}
                  </span>
                </h3>
                  <el-button
                    type="text"
                    :icon="group.isCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"
                    @click="toggleGroup(groupIndex)"
                  />
                </div>
                <div class="knowits-list" v-show="!group.isCollapsed">
                  <Post
                    v-for="knowit in group.knowits || []"
                    :key="knowit?.knowit_id || groupIndex + '-' + index"
                    :post="knowit.post_info"
                    @like="handleLike"
                    @comment="handleComment"
                  />
                </div>
              </div>
              <div v-if="!collection.groups?.length" class="no-data">
                <i class="el-icon-document"></i>
                <p>No Knowits in this collection yet</p>
              </div>
            </div>
          </template>
        </el-card>
      </el-main>
    </div>
  </template>
  
  <script>
  import Post from '@/components/Post.vue'
  import { postRequest } from '@/apis/front'
  
  export default {
    name: 'MyCollection',
    components: {
      Post
    },
    data() {
      return {
        loading: true,
        collection: {
          groups: [],
          title: '',
          description: '',
          user_name: '',
          views: 0,
          likes: 0,
          purchased: 0,
          price: 0,
          content_count: 0
        }
      }
    },
    created() {
      this.fetchCollectionData()
    },
    computed: {
      areAllGroupsCollapsed() {
        return this.collection.groups?.every(group => group.isCollapsed) ?? false
      }
    },
    methods: {
      async fetchCollectionData() {
        this.loading = true
        try {
          const response = await postRequest('getPostCollection', {
            id: this.$route.query.id,
            user_id: this.$store.state.user_id
          })
          
          if (response.data.code === 0) {
            const collectionData = response.data.data
            const groupsWithCollapse = (collectionData.groups || []).map(group => ({
              ...group,
              isCollapsed: false
            }))
            
            this.collection = {
              ...collectionData,
              groups: groupsWithCollapse,
              views: collectionData.views || 0,
              likes: collectionData.likes || 0,
              purchased: collectionData.purchased || 0
            }
          } else {
            this.$message.error(response.data.msg || 'Failed to load collection')
          }
        } catch (error) {
          console.error('Error loading collection:', error)
          this.$message.error('Failed to load collection')
        } finally {
          this.loading = false
        }
      },
      handleLike(postId) {
        console.log('Like post:', postId)
      },
      handleComment(postId) {
        console.log('Comment on post:', postId)
      },
      goBack() {
        this.$router.go(-1)
      },
      handleEdit() {
        localStorage.setItem('editCollectionId', this.$route.query.id)
        this.$router.push('/new-collection')
      },
      toggleGroup(groupIndex) {
        this.$set(this.collection.groups[groupIndex], 'isCollapsed', !this.collection.groups[groupIndex].isCollapsed)
      },
      toggleAllGroups() {
        const newState = !this.areAllGroupsCollapsed
        this.collection.groups.forEach((group, index) => {
          this.$set(this.collection.groups[index], 'isCollapsed', newState)
        })
      }
    }
  }
  </script>
  
  <style scoped>
  .collection-detail-container {
    width: 100%;
  }
  
  .el-header {
    background-color: #409EFF;
    color: white;
    text-align: center;
    padding: 20px 0;
  }
  
  .collection-info-card {
    width: 70%;
    margin: 20px auto;
  }
  
  .collection-header {
    display: flex;
    gap: 20px;
    margin-bottom: 30px;
    position: relative;
  }
  
  .collection-info {
    flex: 1;
  }
  
  .collection-info h2 {
    margin: 0 0 10px 0;
  }
  
  .author {
    color: #606266;
    margin: 0 0 10px 0;
  }
  
  .description {
    color: #303133;
    line-height: 1.6;
  }
  
  .collection-stats {
    display: flex;
    justify-content: space-between;
    padding: 20px 0;
    border-top: 1px solid #ebeef5;
    border-bottom: 1px solid #ebeef5;
    margin-bottom: 30px;
  }
  
  .stats-left {
    display: flex;
    gap: 30px;
  }
  
  .stats-right {
    display: flex;
    gap: 30px;
  }
  
  .stat-item {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #606266;
  }
  
  .groups-container {
    margin-top: 30px;
  }
  
  .group-section {
    margin-bottom: 40px;
  }
  
  .group-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0;
    margin-bottom: 20px;
    border-bottom: 2px solid #409EFF;
  }
  
  .group-header h3 {
    margin: 0;
    color: #409EFF;
    font-size: 20px;
  }
  
  .knowit-count {
    color: #fff;
  background-color: #409EFF;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 6px;
  margin-left: 8px;
  display: inline-block;
  }
  
  .knowits-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
  }
  
  .header-content {
    display: flex;
    align-items: center;
    position: relative;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    min-height: 100px;
  }
  
  .back-button {
    position: absolute;
    left: 20px;
    color: white;
    border: 1px solid white;
    background: transparent;
    z-index: 1;
  }
  
  .back-button:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border-color: white;
  }
  
  h1 {
    width: 100%;
    text-align: center;
    margin: 0;
    position: relative;
    z-index: 0;
  }
  
  .edit-button-container {
    position: absolute;
    top: 0;
    right: 0;
  }
  
  .cover-image {
    width: 200px;
    height: 283px;
    margin-bottom: 0;
    overflow: hidden;
    border-radius: 8px;
    flex-shrink: 0;
    background-color: #f5f7fa;
    border: 1px solid #dcdfe6;
  }
  
  .placeholder-cover {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .placeholder-cover i {
    font-size: 48px;
    color: #909399;
  }
  
  .cover-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .token-price {
    color: #409EFF;
    font-weight: bold;
    font-size: 1.1em;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 10px;
  }
  
  .group-header .el-button {
    padding: 0 10px;
    font-size: 16px;
  }
  
  .groups-header {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 20px;
  }
  
  .groups-header .el-button {
    font-size: 14px;
  }
  
  .loading-container {
    min-height: 400px;
  }
  </style> 